/**
 * KYC DELETE CONFIRMATION MODAL
 * 
 * Modal for confirming deletion of rejected KYC submissions
 * Shows user details and warning about permanent deletion
 */

import React, { useState } from 'react';

interface KYCDeleteConfirmationModalProps {
  isOpen: boolean;
  kycSubmission: {
    id: string;
    user_id: number;
    full_legal_name: string;
    users?: {
      email: string;
      username: string;
    };
  } | null;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const KYCDeleteConfirmationModal: React.FC<KYCDeleteConfirmationModalProps> = ({
  isOpen,
  kycSubmission,
  onConfirm,
  onCancel,
  isLoading = false
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);

  if (!isOpen || !kycSubmission) return null;

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      setConfirmText('');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCancel = () => {
    setConfirmText('');
    onCancel();
  };

  const isConfirmDisabled = confirmText !== 'DELETE' || isLoading || isConfirming;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-red-500/30 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="text-3xl">⚠️</div>
          <h2 className="text-xl font-bold text-red-400">Delete KYC Submission</h2>
        </div>

        {/* Warning Message */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
          <p className="text-red-300 text-sm">
            <strong>Warning:</strong> This action will permanently delete the user's KYC submission and all associated documents. 
            The user will be able to resubmit their KYC from scratch. This action cannot be undone.
          </p>
        </div>

        {/* User Details */}
        <div className="bg-gray-800/50 rounded-lg p-4 mb-4 space-y-2">
          <div>
            <p className="text-white text-sm">Name</p>
            <p className="text-white font-medium">{kycSubmission.full_legal_name}</p>
          </div>
          <div>
            <p className="text-white text-sm">Email</p>
            <p className="text-white font-medium">{kycSubmission.users?.email || 'N/A'}</p>
          </div>
          <div>
            <p className="text-white text-sm">Username</p>
            <p className="text-white font-medium">@{kycSubmission.users?.username || 'N/A'}</p>
          </div>
          <div>
            <p className="text-white text-sm">KYC ID</p>
            <p className="text-white font-mono text-sm">{kycSubmission.id}</p>
          </div>
        </div>

        {/* Confirmation Input */}
        <div className="mb-4">
          <label className="block text-sm text-white mb-2">
            Type <strong className="text-red-400">DELETE</strong> to confirm:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            placeholder="Type DELETE"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-400"
            disabled={isLoading || isConfirming}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            disabled={isLoading || isConfirming}
            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
          >
            {isConfirming ? '🗑️ Deleting...' : '🗑️ Confirm Delete'}
          </button>
        </div>

        {/* Info Text */}
        <p className="text-sm text-gray-500 mt-4 text-center">
          The user will receive an email notification to resubmit their KYC.
        </p>
      </div>
    </div>
  );
};

export default KYCDeleteConfirmationModal;

